/**
 * 双盘对照。两个人的四柱并排陈列，保存走后端 hepan.create / hepan.list。
 *
 * 措辞注意：旧站此处措辞是「相处风格 / 性格适配」，刻意不写「姻缘吉凶」「桃花」
 * 一类的判断。后端 normalizeHepan 有个 score 字段（默认 82），
 * 但对外不呈现为「匹配度打分」——那属于对现实关系下结论，超出备案的服务类别。
 */
import { useMemo, useState } from "react";
import { Solar } from "lunar-javascript";
import { callApi, getSession } from "../api";
import { legalNotice } from "../data/site";

type Person = { name: string; date: string; time: string; sex: string };

const EMPTY_A: Person = { name: "", date: "1990-06-15", time: "14:30", sex: "男" };
const EMPTY_B: Person = { name: "", date: "1992-03-08", time: "09:00", sex: "女" };

function computePillars(person: Person): { gan: string; zhi: string }[] | null {
  const [y, m, d] = person.date.split("-").map(Number);
  const [hh, mm] = person.time.split(":").map(Number);
  if (![y, m, d, hh, mm].every((n) => Number.isFinite(n))) return null;
  const eightChar = Solar.fromYmdHms(y, m, d, hh, mm, 0).getLunar().getEightChar();
  return [eightChar.getYear(), eightChar.getMonth(), eightChar.getDay(), eightChar.getTime()].map(
    (value) => ({ gan: value.slice(0, 1), zhi: value.slice(1) }),
  );
}

const LABELS = ["年柱", "月柱", "日柱", "时柱"];

export function HepanPage({ onNavigate }: { onNavigate: (route: string) => void }) {
  const [personA, setPersonA] = useState<Person>(EMPTY_A);
  const [personB, setPersonB] = useState<Person>(EMPTY_B);
  const [relation, setRelation] = useState("婚恋关系");
  const [shown, setShown] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveError, setSaveError] = useState(false);
  const [busy, setBusy] = useState(false);

  const chartA = useMemo(() => (shown ? computePillars(personA) : null), [shown, personA]);
  const chartB = useMemo(() => (shown ? computePillars(personB) : null), [shown, personB]);

  async function save() {
    if (!chartA || !chartB) return;
    if (!getSession()) {
      setSaveError(true);
      setSaveMessage("合盘记录保存在账号下，请先登录。");
      return;
    }
    setBusy(true);
    const result = await callApi("hepan.create", {
      record: {
        title: `${personA.name || "甲方"} × ${personB.name || "乙方"}`,
        relation,
        personA: {
          name: personA.name,
          sex: personA.sex,
          birthAt: `${personA.date} ${personA.time}`,
          tg: chartA.map((p) => p.gan),
          dz: chartA.map((p) => p.zhi),
        },
        personB: {
          name: personB.name,
          sex: personB.sex,
          birthAt: `${personB.date} ${personB.time}`,
          tg: chartB.map((p) => p.gan),
          dz: chartB.map((p) => p.zhi),
        },
        summary: "",
        tags: [],
      },
    });
    setBusy(false);
    setSaveError(!result.ok);
    setSaveMessage(result.ok ? "已保存到合盘记录。" : result.error);
  }

  function field(person: Person, set: (next: Person) => void, title: string) {
    return (
      <fieldset className="hepan-person">
        <legend>{title}</legend>
        <label>
          称呼
          <input
            type="text"
            value={person.name}
            maxLength={80}
            placeholder="可留空"
            onChange={(e) => set({ ...person, name: e.target.value })}
          />
        </label>
        <label>
          出生日期（公历）
          <input type="date" value={person.date} onChange={(e) => set({ ...person, date: e.target.value })} />
        </label>
        <label>
          出生时间
          <input type="time" value={person.time} onChange={(e) => set({ ...person, time: e.target.value })} />
        </label>
        <label>
          性别
          <select value={person.sex} onChange={(e) => set({ ...person, sex: e.target.value })}>
            <option value="男">男</option>
            <option value="女">女</option>
          </select>
        </label>
      </fieldset>
    );
  }

  function pillarTable(chart: { gan: string; zhi: string }[], person: Person, title: string) {
    return (
      <div className="hepan-chart">
        <h3>
          {person.name || title}
          <span className="famous-sex">{person.sex}</span>
        </h3>
        <table className="pillar-table">
          <thead>
            <tr>
              {LABELS.map((label) => (
                <th key={label} scope="col">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {chart.map((p, i) => (
                <td key={`g${i}`} className="pillar-gan">
                  {p.gan}
                </td>
              ))}
            </tr>
            <tr>
              {chart.map((p, i) => (
                <td key={`z${i}`} className="pillar-zhi">
                  {p.zhi}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <main className="page hepan-page">
      <h1>双盘对照</h1>
      <p className="page-summary">
        把两个人的四柱并排陈列，便于对照天干地支的异同，用于学习与记录整理。
      </p>

      <form
        className="hepan-form"
        onSubmit={(e) => {
          e.preventDefault();
          setSaveMessage("");
          setShown(true);
        }}
      >
        {field(personA, setPersonA, "甲方")}
        {field(personB, setPersonB, "乙方")}
        <label className="hepan-relation">
          关系
          <select value={relation} onChange={(e) => setRelation(e.target.value)}>
            <option value="婚恋关系">婚恋关系</option>
            <option value="亲子关系">亲子关系</option>
            <option value="合作关系">合作关系</option>
            <option value="朋友关系">朋友关系</option>
          </select>
        </label>
        <button type="submit">对照</button>
      </form>

      {chartA && chartB && (
        <section className="hepan-result" aria-live="polite">
          <div className="hepan-charts">
            {pillarTable(chartA, personA, "甲方")}
            {pillarTable(chartB, personB, "乙方")}
          </div>
          <div className="paipan-save">
            <button type="button" onClick={save} disabled={busy}>
              {busy ? "保存中…" : "保存合盘记录"}
            </button>
            {saveMessage && (
              <span className={saveError ? "form-error" : "save-ok"}>{saveMessage}</span>
            )}
            {saveError && !getSession() && (
              <button type="button" className="link" onClick={() => onNavigate("auth")}>
                去登录
              </button>
            )}
          </div>
          <p className="paipan-notice">{legalNotice}</p>
        </section>
      )}
    </main>
  );
}
